import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { TrustMini } from '@/components/TrustBadges';
import Lightbox from '@/components/Lightbox';
import { providerPrice } from '@/lib/pricing';
import {
  Lightning, PaperPlaneRight, Plus, PaperPlaneTilt, Trash, DotsThree,
  PencilSimple, ShareNetwork, PushPin, X, Star, ChatCircle, ArrowRight,
  Crown, Tag, TrendUp, ShieldCheck, CalendarBlank, MapPin, Clock, Fire,
  Sparkle, Users, CurrencyCircleDollar, Check, FolderSimple, CaretDown, CaretRight,
  ArrowCounterClockwise, CaretLeft, ArrowsOut, ArrowsIn,
  Cake, Diamond, Briefcase, House, Gift, GraduationCap, Hammer, Buildings,
  ForkKnife, Headphones, Camera, Martini, Microphone, PersonSimpleThrow, Broom,
  HandWaving, Confetti
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STARTERS = [
  { Icon: Cake, label: 'Birthday party', prompt: "I'm planning a birthday party" },
  { Icon: Diamond, label: 'Wedding', prompt: "I'm planning a wedding" },
  { Icon: Briefcase, label: 'Corporate event', prompt: "I need to organize a corporate event" },
  { Icon: House, label: 'House party', prompt: "I'm throwing a house party" },
  { Icon: Gift, label: 'Baby shower', prompt: "I'm planning a baby shower" },
  { Icon: GraduationCap, label: 'Graduation', prompt: "I'm planning a graduation celebration" },
];

const QUICK_REPLIES = [
  "What's my total?",
  "Swap to a cheaper option",
  "Add a photographer",
  "Show me more venues",
  "I want to change the budget",
  "Book all providers",
];

const LABEL_STYLES = {
  'Best value': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <Tag size={10} weight="fill" /> },
  'Premium pick': { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', icon: <Crown size={10} weight="fill" /> },
  'Most popular': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: <TrendUp size={10} weight="fill" /> },
  'Top rated': { bg: 'bg-secondary', text: 'text-primary', border: 'border-primary/20', icon: <Star size={10} weight="fill" /> },
  'Budget friendly': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <Tag size={10} weight="fill" /> },
  "Editor's choice": { bg: 'bg-accent-coral/10', text: 'text-accent-coral', border: 'border-accent-coral/20', icon: <Crown size={10} weight="fill" /> },
};

const CAT_IMAGES = {
  'Venues': 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=120&h=80&fit=crop',
  'Catering': 'https://images.unsplash.com/photo-1555244162-803834f70033?w=120&h=80&fit=crop',
  'DJs': 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=120&h=80&fit=crop',
  'Photographers': 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=120&h=80&fit=crop',
  'Videographers': 'https://images.unsplash.com/photo-1579632652768-6cb9dcf85912?w=120&h=80&fit=crop',
  'Bartenders': 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=120&h=80&fit=crop',
  'Event Decor': 'https://images.unsplash.com/photo-1478146059778-26028b07395a?w=120&h=80&fit=crop',
  'Live Bands': 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=120&h=80&fit=crop',
  'Private Chefs': 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=120&h=80&fit=crop',
};

// ─── Parsers ───
function parseAiContent(content) {
  const cardRegex = /PROVIDER_CARD\[([^\]]+)\]/g;
  const bookRegex = /BOOK_LINK\[([^\]|]+)\|([^\]]*)\]/g;
  const timelineRegex = /TIMELINE_ITEM\[([^\]]+)\]/g;
  let cleaned = content;
  const cards = [];
  const timeline = [];
  let m;
  while ((m = cardRegex.exec(content)) !== null) {
    const p = m[1].split('|');
    if (p.length >= 8) {
      // Normalize price + unit so downstream renders never double up the "$" or
      // the "per". The AI sometimes emits "$25.0" for price and "per person" for
      // unit → strip the leading $ from price, and the leading "per " from unit.
      const rawPrice = (p[2] || '').trim();
      const price = rawPrice.replace(/^\$/, '').replace(/,/g, '').trim();
      const unit = (p[3] || '').trim().replace(/^per\s+/i, '');
      cards.push({ name: p[0].trim(), category: (p[1] || '').trim(), price, unit, rating: p[4], reviews: p[5], desc: p[6], profileId: p[7], serviceId: p[8] || '', label: p[9] || '', qty: Number(p[10]) || null });
    }
    cleaned = cleaned.replace(m[0], '');
  }
  while ((m = timelineRegex.exec(content)) !== null) {
    const p = m[1].split('|');
    if (p.length >= 3) timeline.push({ time: p[0].trim(), title: p[1].trim(), desc: p[2].trim(), icon: (p[3] || 'party').trim() });
    cleaned = cleaned.replace(m[0], '');
  }
  // Event date, availability annotations, actionable suggestions
  let eventDate = null;
  const dm = content.match(/EVENT_DATE\[(\d{4}-\d{2}-\d{2})\]/);
  if (dm) eventDate = dm[1];
  cleaned = cleaned.replace(/EVENT_DATE\[[^\]]*\]/g, '');
  const availability = {};
  const am = content.match(/AVAILABILITY_CHECK\[([^\]|]+)\|([^\]]*)\]/);
  if (am) am[2].split(',').forEach(pair => { const [pid, ok] = pair.split(':'); if (pid) availability[pid.trim()] = ok === '1'; });
  cleaned = cleaned.replace(/AVAILABILITY_CHECK\[[^\]]*\]/g, '');
  const suggestions = [];
  const sugRe = /SUGGESTION\[([^\]|]+)\|([^\]]+)\]/g;
  while ((m = sugRe.exec(content)) !== null) suggestions.push({ label: m[1].trim(), message: m[2].trim() });
  cleaned = cleaned.replace(/SUGGESTION\[[^\]]*\]/g, '');

  cleaned = cleaned.replace(bookRegex, '').replace(/\n{3,}/g, '\n\n').trim();

  // Parse OPTION_BUTTONS
  const buttonGroups = [];
  const btnRegex = /OPTION_BUTTONS\[([^\]]+)\]([\s\S]*?)END_BUTTONS/g;
  const btnMatches = [...cleaned.matchAll(btnRegex)];
  for (const bm of btnMatches) {
    const question = bm[1].trim();
    const btnText = bm[2];
    const buttons = [];
    const singleBtn = /BUTTON\[([^\]]+)\]/g;
    let sb;
    while ((sb = singleBtn.exec(btnText)) !== null) {
      buttons.push(sb[1].trim());
    }
    if (buttons.length > 0) buttonGroups.push({ question, buttons });
  }
  // Remove all matched button blocks from text
  for (const bm of btnMatches) {
    cleaned = cleaned.replace(bm[0], '');
  }
  // Also clean any stray BUTTON[] or END_BUTTONS that weren't in a group
  cleaned = cleaned.replace(/OPTION_BUTTONS\[[^\]]*\]/g, '').replace(/BUTTON\[[^\]]*\]/g, '').replace(/END_BUTTONS/g, '');
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();

  return { text: cleaned, cards, timeline, buttonGroups, eventDate, availability, suggestions };
}

// Hide half-written markers while a response is still streaming in
const MARKER_TOKENS = ['PROVIDER_CARD[', 'TIMELINE_ITEM[', 'OPTION_BUTTONS[', 'BUTTON[', 'END_BUTTONS', 'SUGGESTION[', 'EVENT_DATE[', 'AVAILABILITY_CHECK['];
function sanitizePartial(c) {
  const lastOB = c.lastIndexOf('OPTION_BUTTONS[');
  if (lastOB !== -1 && !c.slice(lastOB).includes('END_BUTTONS')) c = c.slice(0, lastOB);
  c = c.replace(/(PROVIDER_CARD\[|TIMELINE_ITEM\[|OPTION_BUTTONS\[|BUTTON\[|SUGGESTION\[|EVENT_DATE\[|AVAILABILITY_CHECK\[)[^\]]*$/, '');
  // A marker keyword arriving letter-by-letter ("PROVIDE", "EVENT_DA"…) briefly
  // shows as stray text — trim any tail that could still become a marker.
  const maxLen = Math.max(...MARKER_TOKENS.map(m => m.length));
  for (let i = Math.min(c.length, maxLen - 1); i > 0; i--) {
    const tail = c.slice(-i);
    if (MARKER_TOKENS.some(m => m.length > tail.length && m.startsWith(tail))) {
      c = c.slice(0, c.length - i);
      break;
    }
  }
  return c;
}

// ─── Compact Provider Row (tabbed plan view) — click to expand details inline ───
function CompactProviderRow({ card, avail, availDate, eventId, onRemove }) {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const bookUrl = card.serviceId ? `/checkout/${card.profileId}/${card.serviceId}` : `/provider/${card.profileId}`;
  // Real provider user id comes from the profile fetch; tag the conversation with the event
  const msgUrl = details?.user_id
    ? `/messages/${details.user_id}${eventId ? `?event=${eventId}` : ''}`
    : `/provider/${card.profileId}`;
  const initials = card.name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

  // Fetch full profile up front: avatar for the row, portfolio/reviews for the expansion
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/providers/${card.profileId}`)
      .then(r => { if (alive) setDetails(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [card.profileId]);

  const toggle = () => setOpen(o => !o);
  const avatarUrl = details?.user?.avatar_url || null;
  const photos = (details?.portfolio_images || []).slice(0, 6);

  const d = details;
  // Single source of truth (matches Browse / profile). Falls back to the AI
  // card's own price/unit only when we have no fetched profile yet.
  const rate = d
    ? (() => { const pr = providerPrice(d); return pr.isQuote ? 'Custom quote' : (pr.display || `$${pr.amount}${pr.suffixShort || `/${card.unit}`}`); })()
    : `$${card.price}/${card.unit}`;
  const reviewCount = d?.review_count ?? card.reviews ?? 0;
  const writtenReviews = (d?.reviews || []).filter(r => r.comment).slice(0, 3);
  const reviewsHref = `/provider/${card.profileId}?tab=reviews`;

  return (
    <div data-testid={`provider-card-${card.profileId}`}>
      {/* Collapsed row — click anywhere to expand */}
      <div
        role="button" tabIndex={0} aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-secondary/40 transition-colors cursor-pointer"
        data-testid={`expand-${card.profileId}`}
      >
        {avatarUrl ? (
          <img src={avatarUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${avatarUrl}` : avatarUrl} alt={card.name}
            className="w-9 h-9 rounded-lg object-cover shrink-0 border border-border/60" />
        ) : (
          <span className="w-9 h-9 rounded-lg bg-secondary text-primary text-[10px] font-bold flex items-center justify-center shrink-0">{initials}</span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-[12px] font-semibold text-foreground truncate">{card.name}</p>
            <span className="flex items-center gap-0.5 shrink-0">
              <Star size={9} weight="fill" className="text-amber-400" />
              <span className="text-[10px] text-muted-foreground">{card.rating}{reviewCount ? ` (${reviewCount})` : ''}</span>
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground truncate flex items-center gap-1.5">
            {card.category}{card.label ? ` · ${card.label}` : ''}
            {avail !== undefined && availDate && (
              avail
                ? <span className="inline-flex items-center gap-0.5 font-medium text-emerald-600 shrink-0"><Check size={8} weight="bold" /> Available {availDate}</span>
                : <span className="inline-flex items-center gap-0.5 font-medium text-red-500 shrink-0"><X size={8} weight="bold" /> Booked {availDate}</span>
            )}
          </p>
        </div>
        <span className="text-[12px] font-semibold text-foreground tabular-nums shrink-0">
          ${card.price}<span className="text-[9px] font-normal text-muted-foreground">/{card.unit}</span>
        </span>
        <a href={bookUrl} onClick={(e) => e.stopPropagation()} className="bg-primary text-primary-foreground text-[10px] font-semibold px-3 py-2 rounded-lg btn-anim hover:bg-primary/90 no-underline shrink-0" data-testid={`book-${card.profileId}`}>Book</a>
        {onRemove && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(card); }}
            aria-label={`Remove ${card.name} from plan`}
            title="Remove from plan"
            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 btn-anim cursor-pointer"
            data-testid={`remove-${card.profileId}`}
          >
            <X size={13} weight="bold" />
          </button>
        )}
        <ArrowRight size={12} className={`text-muted-foreground shrink-0 transition-transform duration-200 ${open ? 'rotate-90 text-primary' : ''}`} />
      </div>

      {/* Expanded details — inline, no navigation */}
      {open && (
        <div className="px-4 pb-4 pt-1 bg-secondary/20 border-t border-border/40 fade-up" data-testid={`details-${card.profileId}`}>
          {loading && !d ? (
            <div className="space-y-2 py-2">
              <div className="h-3 bg-secondary/70 rounded-xl animate-pulse w-3/4" />
              <div className="h-3 bg-secondary/70 rounded-xl animate-pulse w-1/2" />
            </div>
          ) : (
            <>
              {/* Full description */}
              <p className="text-[11px] text-foreground/80 leading-relaxed mt-2 mb-3">{d?.description || card.desc}</p>

              {/* Provider photos */}
              {photos.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-1 mb-3" data-testid={`photos-${card.profileId}`}>
                  {photos.map((src, i) => (
                    <button key={i} type="button"
                      onClick={(e) => { e.stopPropagation(); setLightbox(i); }}
                      aria-label={`Open photo ${i + 1} of ${card.name}`}
                      className="shrink-0 rounded-lg cursor-zoom-in btn-anim hover:shadow-lift focus:outline-none focus:ring-2 focus:ring-primary/50">
                      <img
                        src={src.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src}
                        alt={`${card.name} work ${i + 1}`}
                        loading="lazy"
                        className="h-20 w-28 object-cover rounded-lg border border-border/60 pointer-events-none" />
                    </button>
                  ))}
                </div>
              )}
              {lightbox !== null && (
                <Lightbox images={photos} startIndex={lightbox} onClose={() => setLightbox(null)} alt={card.name} />
              )}

              {/* Stat grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                {reviewCount > 0 ? (
                  <a href={reviewsHref} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                    className="bg-card border border-border/60 rounded-lg px-2.5 py-2 no-underline hover:border-primary/50 hover:bg-secondary/40 transition-colors group/rating" data-testid={`rating-link-${card.profileId}`}>
                    <p className="text-[8px] uppercase tracking-wider text-muted-foreground mb-0.5">Rating</p>
                    <p className="text-[11px] font-semibold text-foreground flex items-center gap-1">
                      <Star size={10} weight="fill" className="text-amber-400" />
                      {d?.rating_avg ?? card.rating} <span className="font-normal text-primary group-hover/rating:underline">({reviewCount} reviews)</span>
                    </p>
                  </a>
                ) : (
                  <div className="bg-card border border-border/60 rounded-lg px-2.5 py-2">
                    <p className="text-[8px] uppercase tracking-wider text-muted-foreground mb-0.5">Rating</p>
                    <p className="text-[11px] font-semibold text-foreground flex items-center gap-1">
                      <Star size={10} weight="fill" className="text-amber-400" />
                      {d?.rating_avg ?? card.rating} <span className="font-normal text-muted-foreground">(0 reviews)</span>
                    </p>
                  </div>
                )}
                <div className="bg-card border border-border/60 rounded-lg px-2.5 py-2">
                  <p className="text-[8px] uppercase tracking-wider text-muted-foreground mb-0.5">Rate</p>
                  <p className="text-[11px] font-semibold text-foreground tabular-nums">{rate}</p>
                </div>
                <div className="bg-card border border-border/60 rounded-lg px-2.5 py-2">
                  <p className="text-[8px] uppercase tracking-wider text-muted-foreground mb-0.5">Location</p>
                  <p className="text-[11px] font-semibold text-foreground flex items-center gap-1">
                    <MapPin size={10} className="text-primary" />
                    {d?.city || '—'}{d?.service_radius_km ? ` · ${d.service_radius_km}km` : ''}
                  </p>
                </div>
                <div className="bg-card border border-border/60 rounded-lg px-2.5 py-2">
                  <p className="text-[8px] uppercase tracking-wider text-muted-foreground mb-0.5">{d?.capacity ? 'Capacity' : d?.verified ? 'Status' : 'Reviews'}</p>
                  <p className="text-[11px] font-semibold text-foreground flex items-center gap-1">
                    {d?.capacity ? (<><Users size={10} className="text-primary" /> {d.capacity} guests</>)
                      : d?.verified ? (<><ShieldCheck size={10} weight="fill" className="text-primary" /> Verified</>)
                      : (<><Star size={10} weight="fill" className="text-amber-400" /> {d?.review_count ?? card.reviews} reviews</>)}
                  </p>
                </div>
              </div>

              {/* Written reviews — click through to read them all */}
              {writtenReviews.length > 0 ? (
                <div className="space-y-1.5 mb-3" data-testid={`reviews-${card.profileId}`}>
                  {writtenReviews.map((r, i) => (
                    <div key={i} className="bg-card border border-border/60 rounded-lg px-3 py-2">
                      <span className="flex items-center gap-0.5 mb-0.5">
                        {[...Array(Math.round(r.rating || 5))].map((_, s) => <Star key={s} size={8} weight="fill" className="text-amber-400" />)}
                      </span>
                      <p className="text-[10px] text-foreground/75 leading-relaxed line-clamp-2">"{r.comment}"</p>
                    </div>
                  ))}
                  <a href={reviewsHref} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary hover:underline no-underline" data-testid={`see-reviews-${card.profileId}`}>
                    See all {reviewCount} reviews <ArrowRight size={9} />
                  </a>
                </div>
              ) : reviewCount > 0 ? (
                <a href={reviewsHref} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary hover:underline no-underline mb-3" data-testid={`see-reviews-${card.profileId}`}>
                  Read {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'} <ArrowRight size={9} />
                </a>
              ) : (
                <p className="text-[10px] text-muted-foreground mb-3">No reviews yet</p>
              )}

              {/* Actions — links, chat stays put */}
              <div className="flex gap-1.5">
                <a href={bookUrl} onClick={(e) => e.stopPropagation()} className="flex-1 bg-primary text-primary-foreground text-center py-2 text-[10px] font-semibold hover:bg-primary/90 btn-anim shadow-soft no-underline rounded-lg">Book now — ${card.price}/{card.unit}</a>
                <a href={msgUrl} onClick={(e) => e.stopPropagation()} className="border border-border bg-card text-muted-foreground py-2 px-3 text-[10px] font-semibold hover:bg-secondary/60 hover:text-primary btn-anim no-underline flex items-center gap-1 rounded-lg" data-testid={`msg-${card.profileId}`}>
                  <ChatCircle size={10} /> Message
                </a>
                <a href={`/provider/${card.profileId}`} onClick={(e) => e.stopPropagation()} className="border border-border bg-card text-muted-foreground py-2 px-3 text-[10px] font-semibold hover:bg-secondary/60 hover:text-primary btn-anim no-underline flex items-center rounded-lg">
                  Full profile
                </a>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Timeline Icons ───
const TIMELINE_ICONS = {
  setup: Hammer, venue: Buildings, food: ForkKnife, music: Headphones, photo: Camera,
  drinks: Martini, speech: Microphone, dance: PersonSimpleThrow, cake: Cake, cleanup: Broom,
  arrival: HandWaving, ceremony: Diamond, party: Confetti, default: Clock,
};

// ─── Event Timeline Component ───
function EventTimeline({ items, bare = false }) {
  const [expanded, setExpanded] = useState(true);
  if (!items.length) return null;

  return (
    <div data-testid="event-timeline">
      {!bare && (
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2.5 mb-3 mt-6 w-full text-left group cursor-pointer">
        <span className="w-7 h-7 rounded-lg bg-secondary text-primary flex items-center justify-center shrink-0">
          <CalendarBlank size={14} weight="fill" />
        </span>
        <span className="text-[13px] font-semibold text-foreground tracking-tight">Event timeline</span>
        <div className="flex-1 h-px bg-border/70" />
        <span className="text-[10px] text-muted-foreground group-hover:text-primary transition-colors shrink-0">{items.length} steps &middot; {expanded ? 'Hide' : 'Show'}</span>
      </button>
      )}

      {expanded && (
        <div className="relative pl-6 border-l-2 border-primary/20 space-y-0" data-testid="timeline-items">
          {items.map((item, i) => {
            const TimelineIcon = TIMELINE_ICONS[item.icon] || TIMELINE_ICONS.default;
            const isFirst = i === 0;
            const isLast = i === items.length - 1;

            return (
              <div key={i} className="relative pb-4 last:pb-0 group fade-up" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }} data-testid={`timeline-${i}`}>
                {/* Dot on the line */}
                <div className="absolute -left-[25px] top-0.5 w-4 h-4 bg-card border-2 border-primary/40 rounded-full flex items-center justify-center group-hover:border-primary transition-colors">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                </div>

                <div className="ml-2 bg-card border border-border/70 rounded-xl px-3.5 py-2.5 shadow-soft hover:shadow-lift hover:border-primary/30 transition-all">
                  <div className="flex items-start gap-2.5">
                    <TimelineIcon size={17} weight="regular" className="text-primary shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[11px] font-bold text-primary bg-secondary px-1.5 py-0.5 rounded font-mono">{item.time}</span>
                        <span className="text-[12px] font-semibold text-foreground">{item.title}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* End marker */}
          <div className="absolute -left-[21px] bottom-0 w-2.5 h-2.5 bg-primary rounded-full" />
        </div>
      )}
    </div>
  );
}

// ─── Plan Section Header ───
function SectionHeader({ icon, title, meta, action }) {
  return (
    <div className="flex items-center gap-2.5 mb-3 mt-6 first:mt-0">
      <span className="w-7 h-7 rounded-lg bg-secondary text-primary flex items-center justify-center shrink-0">{icon}</span>
      <h4 className="text-[13px] font-semibold text-foreground tracking-tight">{title}</h4>
      <div className="flex-1 h-px bg-border/70" />
      {meta && <span className="text-[10px] text-muted-foreground shrink-0">{meta}</span>}
      {action}
    </div>
  );
}

// ─── Budget Breakdown parser: pulls line items out of the AI's BUDGET BREAKDOWN block ───
function parseBudget(content) {
  const m = content.match(/BUDGET BREAKDOWN\**([\s\S]*?)(?=\*\*WHAT'S INCLUDED|WHAT'S INCLUDED|TIMELINE_ITEM|\*\*YOUR [A-Z]|$)/i);
  if (!m) return null;
  const block = m[1];
  const items = [];
  let subtotal = null, fee = null, total = null;
  for (const raw of block.split('\n')) {
    const line = raw.trim();
    const lm = line.match(/^-?\s*\**([^:*]+?)\**\s*:\s*\**\$([\d,]+(?:\.\d+)?)/);
    if (!lm) continue;
    const label = lm[1].replace(/\*/g, '').trim();
    const val = lm[2];
    if (/subtotal/i.test(label)) subtotal = val;
    else if (/fee/i.test(label)) fee = val;
    else if (/^total/i.test(label)) total = val;
    else if (/budget|remaining/i.test(label)) continue; // handled separately below
    else {
      // "Catering — Maison Verte ($48 × 150)" → service / name / rate columns
      const paren = label.match(/\(([^)]*)\)\s*$/);
      const rate = paren ? paren[1].trim() : '';
      const base = (paren ? label.slice(0, paren.index) : label).trim();
      const parts = base.split(/\s+[—–]\s*|\s+-\s+/);
      const service = parts[0].trim();
      const name = parts.slice(1).join(' — ').trim();
      items.push({ label, val, service, name, rate: /^flat$/i.test(rate) ? 'Flat rate' : rate });
    }
  }
  // Search the whole message: the "Your budget | Remaining" line often sits at the block boundary
  const remaining = content.match(/(?:Remaining|Under budget)(?:\s*by)?:?\s*\$([\d,]+(?:\.\d+)?)/i)?.[1] || null;
  const budgetAmt = content.match(/budget:?\s*\$([\d,]+(?:\.\d+)?)/i)?.[1] || null;
  if (!items.length) return null;
  return { items, subtotal, fee, total, remaining, budgetAmt };
}

// ─── AI Message Bubble ───
function AiMessageBubble({ content, onBookAll, onQuickReply, streaming, eventId }) {
  const effContent = streaming ? sanitizePartial(content) : content;
  const { text, cards: allCards, timeline, buttonGroups, eventDate, availability, suggestions } = useMemo(() => parseAiContent(effContent), [effContent]);
  const [tab, setTab] = useState('services');
  // Providers the user removed from the plan (by profileId+serviceId key). Kept
  // in local state so the plan updates live without re-asking the AI.
  const [removed, setRemoved] = useState(() => new Set());
  const cardKey = (c) => `${c.profileId}::${c.serviceId || ''}`;
  const cards = useMemo(() => allCards.filter(c => !removed.has(cardKey(c))), [allCards, removed]);
  const removeCard = useCallback((c) => setRemoved(prev => new Set(prev).add(cardKey(c))), []);
  // Full-screen expanded plan view
  const [expanded, setExpanded] = useState(false);
  // Lock background scroll + close on Escape while the full-screen plan is open
  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [expanded]);
  // Multi-question answers: pick one option per question, then submit together
  const [picks, setPicks] = useState({});
  // "Other" free-text answers per question
  const [otherOpen, setOtherOpen] = useState({});
  const [otherText, setOtherText] = useState({});
  // \btotal\b won't match inside "Subtotal", so this finds the real grand total line
  const totalMatch = text.match(/\btotal\b[^\n]*?\$\s?([\d,]+(?:\.\d+)?)/i);
  const totalStr = totalMatch ? totalMatch[1].replace(/,/g, '') : null;
  const hasQuestions = text.includes('?');

  // Group cards by category
  const grouped = useMemo(() => {
    const g = {};
    cards.forEach(c => {
      const cat = c.category;
      if (!g[cat]) g[cat] = [];
      g[cat].push(c);
    });
    return g;
  }, [cards]);

  // Structured plan pieces (full-plan view only)
  const budgetRaw = useMemo(() => parseBudget(effContent), [effContent]);
  const anyRemoved = removed.size > 0;

  // Line-item cost for a single card, from its base price × quantity (qty = hours
  // for hourly, guests for per-person, 1 for flat). Used to re-total the plan
  // after a provider is removed, so the displayed total actually drops.
  const cardCost = useCallback((c) => {
    const base = Number(String(c.price).replace(/[^0-9.]/g, '')) || 0;
    const qty = Number(c.qty) > 0 ? Number(c.qty) : 1;
    return base * qty;
  }, []);
  const cardsTotal = useMemo(() => cards.reduce((s, c) => s + cardCost(c), 0), [cards, cardCost]);

  // When the user has removed a provider, the AI's original budget table/total no
  // longer matches — rebuild the budget from the remaining cards. Otherwise use
  // the AI's parsed breakdown as-is.
  const budget = useMemo(() => {
    if (!anyRemoved) return budgetRaw;
    if (!cards.length) return null;
    const items = cards.map(c => ({
      service: c.category,
      name: c.name,
      rate: `$${c.price}/${c.unit}${Number(c.qty) > 1 ? ` × ${c.qty}` : ''}`,
      val: Math.round(cardCost(c)).toLocaleString(),
      label: c.category,
    }));
    return { items, total: Math.round(cardsTotal).toLocaleString(), remaining: null };
  }, [anyRemoved, budgetRaw, cards, cardsTotal, cardCost]);

  // Single source of truth for the plan total — after a removal, recompute from the
  // remaining cards; otherwise use the AI's parsed breakdown (text fallback second).
  const planTotalRaw = anyRemoved
    ? String(Math.round(cardsTotal))
    : (budgetRaw?.total || totalStr || '').toString().replace(/,/g, '');
  const planTotal = planTotalRaw
    ? Number(planTotalRaw).toLocaleString(undefined, planTotalRaw.includes('.') ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : { maximumFractionDigits: 2 })
    : null;

  // Intro: keep only the AI's opening words — stop at the first section header / divider
  const intro = useMemo(() => {
    const out = [];
    for (const raw of text.split('\n')) {
      const t = raw.trim();
      if (/^\*\*[^*]+\*\*$/.test(t) || t === '---' || /BUDGET BREAKDOWN|YOUR EVENT AT A GLANCE|WHAT'S INCLUDED/i.test(t)) break;
      out.push(raw);
    }
    return out.join('\n').trim();
  }, [text]);

  // Note: the AI's advice after the budget (e.g. "with the $438 left, add lighting…").
  // Stop only at section headers on their own line — inline **bold** must not cut the sentence.
  const note = useMemo(() => {
    const m = text.match(/(?:Remaining[^\n]*|Under budget[^\n]*|on budget[^\n]*)\n+([\s\S]*?)(?=\n\s*\*\*[A-Z][^*\n]*\*\*\s*(?:\n|$)|WHAT'S INCLUDED|YOUR EVENT AT A GLANCE|$)/i);
    const t = m?.[1]?.replace(/^-+\s*$/gm, '').replace(/\*\*/g, '').trim();
    return t && t.length > 20 ? t : null;
  }, [text]);

  const fmt = (s) => s
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
    .replace(/## (.*)/g, '<h4 class="text-[13px] font-semibold text-foreground mt-3 mb-1">$1</h4>')
    .replace(/# (.*)/g, '<h3 class="text-sm font-semibold text-foreground mt-3 mb-1">$1</h3>');

  // If the AI never produced any provider cards, render normal text + button options.
  // (Use allCards, not the removal-filtered cards, so emptying the plan by removing
  // every provider still shows the plan view rather than dumping the raw text.)
  if (allCards.length === 0) {
    return (
      <div>
        <div className="whitespace-pre-wrap leading-relaxed text-[14px] text-foreground/80" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fmt(text)) }} />
        {streaming && <span className="inline-block w-1.5 h-3.5 bg-primary/70 rounded-sm animate-pulse ml-0.5 align-middle" aria-hidden="true" />}
        {/* AI option buttons */}
        {buttonGroups.length > 0 && onQuickReply && (() => {
          const multi = buttonGroups.length > 1;
          // A question is answered by a picked chip OR non-empty "Other" text
          const answerOf = (gi) => (otherOpen[gi] ? (otherText[gi] || '').trim() : (picks[gi] || ''));
          const answered = buttonGroups.filter((_, gi) => answerOf(gi)).length;
          const allAnswered = answered === buttonGroups.length;
          const submitAnswers = () => onQuickReply(
            buttonGroups.map((g, gi) => `${gi + 1}. ${answerOf(gi)}`).join('\n')
          );
          const selectChip = (gi, btn, isSel) => {
            setOtherOpen(o => ({ ...o, [gi]: false }));
            setPicks(p => ({ ...p, [gi]: isSel ? undefined : btn }));
          };
          const toggleOther = (gi) => {
            setPicks(p => ({ ...p, [gi]: undefined }));
            setOtherOpen(o => ({ ...o, [gi]: !o[gi] }));
          };
          return (
            <div className="mt-4 space-y-4" data-testid="option-buttons">
              {buttonGroups.map((group, gi) => {
                const isDateQ = /\b(when|date|day)\b/i.test(group.question);
                return (
                <div key={gi}>
                  <p className="text-[11px] font-medium text-foreground mb-2 flex items-center gap-1.5">
                    {multi && <span className="w-4 h-4 rounded-full bg-primary/10 text-primary text-[9px] font-bold flex items-center justify-center shrink-0">{gi + 1}</span>}
                    {group.question}
                    {multi && answerOf(gi) && <Check size={11} weight="bold" className="text-primary" />}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {/* Date questions use the calendar below instead of preset chips */}
                    {!isDateQ && group.buttons.map((btn, bi) => {
                      const isSel = multi && picks[gi] === btn;
                      return (
                        <button key={bi}
                          onClick={() => multi ? selectChip(gi, btn, isSel) : onQuickReply(btn)}
                          aria-pressed={multi ? isSel : undefined}
                          className={`text-xs font-medium rounded-full px-4 py-2.5 min-h-[44px] btn-anim cursor-pointer fade-up inline-flex items-center gap-1.5 ${isSel
                            ? 'bg-primary text-primary-foreground shadow-lift'
                            : 'bg-secondary text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-lift'}`}
                          style={{ animationDelay: `${Math.min(bi, 8) * 50}ms` }}
                          data-testid={`option-btn-${gi}-${bi}`}>
                          {isSel && <Check size={11} weight="bold" />}{btn}
                        </button>
                      );
                    })}
                    {/* Other — type your own answer (date questions are calendar-only) */}
                    {!isDateQ && (
                    <button type="button"
                      onClick={() => toggleOther(gi)}
                      aria-pressed={!!otherOpen[gi]}
                      className={`text-xs font-medium rounded-full px-4 py-2.5 min-h-[44px] btn-anim cursor-pointer inline-flex items-center gap-1.5 border border-dashed ${otherOpen[gi]
                        ? 'bg-primary text-primary-foreground border-transparent shadow-lift'
                        : 'bg-card text-muted-foreground border-border hover:text-primary hover:border-primary/40'}`}
                      data-testid={`option-other-${gi}`}>
                      <PencilSimple size={11} /> Other…
                    </button>
                    )}
                  </div>
                  {otherOpen[gi] && !isDateQ && (
                    <div className="mt-2 relative max-w-sm fade-up">
                      <input
                        autoFocus
                        type="text"
                        value={otherText[gi] || ''}
                        onChange={e => setOtherText(t => ({ ...t, [gi]: e.target.value }))}
                        onKeyDown={e => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          const v = (otherText[gi] || '').trim();
                          if (!v) return;
                          if (!multi) onQuickReply(v);
                          else if (allAnswered) submitAnswers();
                        }}
                        placeholder="Type your answer…"
                        aria-label={`Your answer to: ${group.question}`}
                        className="w-full bg-card border border-border rounded-full pl-4 pr-11 py-2.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 placeholder:text-muted-foreground/70"
                        data-testid={`other-input-${gi}`}
                      />
                      {!multi && (
                        <button
                          onClick={() => { const v = (otherText[gi] || '').trim(); if (v) onQuickReply(v); }}
                          aria-label="Send answer"
                          className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center btn-anim hover:bg-primary/90 cursor-pointer"
                          data-testid={`other-send-${gi}`}>
                          <ArrowRight size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                  )}
                  {/* Date questions get a real calendar */}
                  {isDateQ && (
                    <div className="mt-2.5">
                      <MiniCalendar
                        selected={multi ? (picks[gi] || null) : null}
                        onSelect={(iso) => {
                          if (multi) {
                            setOtherOpen(o => ({ ...o, [gi]: false }));
                            setPicks(p => ({ ...p, [gi]: iso }));
                          } else {
                            onQuickReply(iso);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              );})}
              {multi && (
                <button
                  disabled={!allAnswered}
                  onClick={submitAnswers}
                  className="w-full bg-primary text-primary-foreground rounded-xl h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-soft cursor-pointer flex items-center justify-center gap-2"
                  data-testid="answers-submit">
                  <PaperPlaneRight size={14} weight="fill" />
                  {allAnswered ? 'Submit answers' : `Answer ${buttonGroups.length - answered} more question${buttonGroups.length - answered > 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          );
        })()}
        {/* Answered/older messages: keep the questions visible as a numbered list */}
        {buttonGroups.length > 0 && !onQuickReply && (
          <div className="mt-3 space-y-1.5" data-testid="asked-questions">
            {buttonGroups.map((group, gi) => (
              <p key={gi} className="text-[12px] font-semibold text-foreground flex items-center gap-1.5">
                {buttonGroups.length > 1 && <span className="w-4 h-4 rounded-full bg-primary/10 text-primary text-[9px] font-bold flex items-center justify-center shrink-0">{gi + 1}</span>}
                {group.question}
              </p>
            ))}
          </div>
        )}
        {/* One-click AI suggestions */}
        {suggestions.length > 0 && onQuickReply && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="suggestions">
            {suggestions.map((sg, i) => (
              <button key={i} onClick={() => onQuickReply(sg.message)}
                className="text-[11px] font-semibold rounded-full bg-accent-coral/10 text-accent-coral px-4 py-2.5 min-h-[44px] hover:bg-accent-coral hover:text-primary-foreground btn-anim cursor-pointer inline-flex items-center gap-1.5"
                data-testid={`suggestion-${i}`}>
                <Plus size={11} weight="bold" /> {sg.label}
              </button>
            ))}
          </div>
        )}

        {/* Fallback quick replies if no button groups */}
        {buttonGroups.length === 0 && hasQuestions && onQuickReply && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="quick-replies">
            {QUICK_REPLIES.slice(0, 4).map((r, i) => (
              <button key={`qr-${i}`} onClick={() => onQuickReply(r)} className="text-[11px] font-medium rounded-full bg-secondary text-primary px-4 py-2.5 min-h-[44px] hover:bg-primary hover:text-primary-foreground btn-anim cursor-pointer" data-testid={`quick-reply-${i}`}>{r}</button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ═══ FULL EVENT PLAN VIEW ═══
  // Shared panel renderers — used by both the compact tabbed card and the
  // full-screen expanded layout. `scroll` controls the max-height cap.
  const servicesPanel = (scroll = 'max-h-[340px]') => (
    cards.length === 0 ? (
      <div className="px-4 py-10 text-center" data-testid="plan-empty">
        <p className="text-[14px] text-muted-foreground">You've removed every provider.</p>
        {onQuickReply && (
          <button onClick={() => onQuickReply('Rebuild my plan')} className="mt-3 text-[13px] font-semibold text-primary hover:underline cursor-pointer">Ask the planner to rebuild →</button>
        )}
      </div>
    ) : (
      <div className={`divide-y divide-border/50 ${scroll} overflow-y-auto`} data-testid="provider-cards">
        {cards.map((card, i) => (
          <div key={cardKey(card)} className="fade-up" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}>
            <CompactProviderRow card={card} avail={availability[card.profileId]} availDate={eventDate} eventId={eventId} onRemove={removeCard} />
          </div>
        ))}
      </div>
    )
  );

  const budgetPanel = (scroll = 'max-h-[340px]') => budget && (
    <div data-testid="budget-breakdown">
      <div className={`${scroll} overflow-y-auto`}>
        <table className="w-full text-[13px]">
          <thead className="sticky top-0 bg-secondary/60 backdrop-blur-sm">
            <tr>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Service</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Name</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold hidden sm:table-cell">Rate</th>
              <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Price</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {budget.items.map((it, i) => (
              <tr key={i}>
                <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{it.service || it.label}</td>
                <td className="px-3 py-2.5 font-medium text-foreground">{it.name || '—'}</td>
                <td className="px-3 py-2.5 text-muted-foreground hidden sm:table-cell whitespace-nowrap">{it.rate || '—'}</td>
                <td className="px-4 py-2.5 text-right font-medium text-foreground tabular-nums whitespace-nowrap">${it.val}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="divide-y divide-border/50 border-t border-border/50">
          <div className="flex items-center justify-between px-4 py-3 bg-secondary/40">
            <span className="text-[14px] font-semibold text-foreground">Total</span>
            <div className="text-right">
              <span className="text-base font-display font-semibold text-foreground tabular-nums">${planTotal}</span>
              {budget.remaining && (
                <p className="text-[10px] font-medium text-emerald-600">Under budget by ${budget.remaining}</p>
              )}
            </div>
          </div>
        </div>
      </div>
      {note && !anyRemoved && (
        <div className="flex gap-2.5 bg-secondary/40 border-t border-border/60 px-4 py-3">
          <Sparkle size={14} weight="fill" className="text-primary shrink-0 mt-0.5" />
          <p className="text-[12px] text-foreground/80 leading-relaxed whitespace-pre-wrap">{note}</p>
        </div>
      )}
    </div>
  );

  const timelinePanel = (scroll = 'max-h-[340px]') => timeline.length > 0 && (
    <div className={`p-4 ${scroll} overflow-y-auto`}>
      <EventTimeline items={timeline} bare />
    </div>
  );

  // Compact inline plan (the default view inside the chat column)
  const planBody = () => (
    <>
      {/* Plan intro — just the AI's opening words, no leftover markup */}
      {intro && <div className="whitespace-pre-wrap leading-relaxed text-[14px] text-foreground/80 mb-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fmt(intro)) }} />}

      {/* Plan summary banner */}
      <div className="bg-primary rounded-2xl p-4 mb-2 text-primary-foreground shadow-soft rise" data-testid="plan-header">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-primary-foreground/15 backdrop-blur rounded-xl flex items-center justify-center shrink-0">
              <Sparkle size={18} weight="fill" className="text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <p className="text-[15px] font-semibold">Your Complete Event Plan</p>
              <p className="text-[11px] text-primary-foreground/70">{cards.length} services &middot; {Object.keys(grouped).length} categories &middot; Everything handled</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {planTotal && (
              <div className="text-right">
                <p className="text-[9px] text-primary-foreground/60 uppercase tracking-wider">Total</p>
                <p className="text-xl font-display font-semibold">${planTotal}</p>
              </div>
            )}
            {/* Open the plan in a full-screen view */}
            <button
              type="button"
              onClick={() => setExpanded(true)}
              aria-label="Open plan in full screen"
              title="Open in full screen"
              className="hidden sm:flex w-9 h-9 items-center justify-center rounded-xl bg-primary-foreground/15 hover:bg-primary-foreground/25 text-primary-foreground btn-anim cursor-pointer shrink-0"
              data-testid="expand-plan"
            >
              <ArrowsOut size={17} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* ═══ TABBED PLAN CARD — everything in one compact panel ═══ */}
      <div className="bg-card border border-border/70 rounded-2xl shadow-soft overflow-hidden rise" style={{ animationDelay: '90ms' }} data-testid="plan-tabs">
        {/* Tab bar */}
        <div className="flex gap-1 bg-secondary/50 p-1.5 border-b border-border/60">
          {[
            ['services', `Services (${cards.length})`],
            ...(budget ? [['budget', 'Budget']] : []),
            ...(timeline.length ? [['timeline', `Timeline (${timeline.length})`]] : []),
          ].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`flex-1 text-[12px] font-semibold py-2.5 rounded-lg btn-anim cursor-pointer ${tab === k ? 'bg-card text-primary shadow-soft' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`plan-tab-${k}`}>
              {l}
            </button>
          ))}
        </div>

        {/* Tab panels — crossfade on switch */}
        <div key={tab} className="tab-fade">
          {tab === 'services' && servicesPanel()}
          {tab === 'budget' && budgetPanel()}
          {tab === 'timeline' && timelinePanel()}
        </div>

        {/* BOOK BAR — always visible under every tab */}
        <div className="bg-foreground p-3.5 flex items-center gap-3 text-background" data-testid="book-all-section">
          <div className="flex-1 min-w-0">
            <p className="text-[9px] text-background/60 flex items-center gap-1.5 mb-0.5">
              <ShieldCheck size={9} /> {cards.length} services{eventDate ? <> &middot; <CalendarBlank size={9} /> {eventDate}</> : null} &middot; escrow protected &middot; free cancel 48h+
            </p>
            {planTotal && <p className="text-lg font-display font-semibold leading-tight">${planTotal}</p>}
          </div>
          <button onClick={() => onBookAll(cards, eventDate)} disabled={cards.length === 0} className="bg-primary text-primary-foreground px-5 py-3 text-[12px] font-semibold hover:bg-primary/90 hover:shadow-lift btn-anim cursor-pointer rounded-xl flex items-center gap-2 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="book-all-button">
            <Lightning size={14} weight="fill" /> Book entire event
          </button>
        </div>
      </div>

      {/* One-click AI suggestions */}
      {suggestions.length > 0 && onQuickReply && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="plan-suggestions">
          {suggestions.map((sg, i) => (
            <button key={i} onClick={() => onQuickReply(sg.message)}
              className="text-[12px] font-semibold rounded-full bg-accent-coral/10 text-accent-coral px-4 py-2.5 min-h-[44px] hover:bg-accent-coral hover:text-primary-foreground btn-anim cursor-pointer inline-flex items-center gap-1.5"
              data-testid={`suggestion-${i}`}>
              <Plus size={11} weight="bold" /> {sg.label}
            </button>
          ))}
        </div>
      )}

      {/* Quick replies for adjustments */}
      {onQuickReply && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="quick-replies">
          {["Swap a provider", "Add more services", "Lower the budget", "Remove something", "Show alternatives", "Add a timeline"].map((r, i) => (
            <button key={i} onClick={() => onQuickReply(r)} className="text-[12px] font-medium rounded-full bg-secondary text-primary px-4 py-2.5 min-h-[44px] hover:bg-primary hover:text-primary-foreground btn-anim cursor-pointer" data-testid={`quick-reply-${i}`}>{r}</button>
          ))}
        </div>
      )}
    </>
  );

  return (
    <>
      <div data-testid="event-plan">{planBody()}</div>

      {/* ═══ FULL-SCREEN EXPANDED PLAN — a real app-like layout that fills the screen ═══ */}
      {/* Portalled to document.body so no ancestor's transform/overflow can trap the
          position:fixed overlay inside the chat column — it truly covers the page. */}
      {expanded && createPortal(
        <div className="fixed inset-0 z-[9999] bg-background flex flex-col" data-testid="plan-modal">
          {/* Sticky header */}
          <div className="bg-primary text-primary-foreground shadow-soft shrink-0">
            <div className="max-w-6xl mx-auto w-full px-5 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 bg-primary-foreground/15 backdrop-blur rounded-xl flex items-center justify-center shrink-0">
                  <Sparkle size={22} weight="fill" className="text-primary-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg font-semibold leading-tight truncate">Your Complete Event Plan</p>
                  <p className="text-[12px] text-primary-foreground/70">
                    {cards.length} services &middot; {Object.keys(grouped).length} categories
                    {eventDate ? <> &middot; {eventDate}</> : null}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                {planTotal && (
                  <div className="text-right">
                    <p className="text-[10px] text-primary-foreground/60 uppercase tracking-wider">Total</p>
                    <p className="text-2xl font-display font-semibold leading-none">${planTotal}</p>
                    {budget?.remaining && <p className="text-[11px] font-medium text-primary-foreground/80 mt-0.5">Under budget by ${budget.remaining}</p>}
                  </div>
                )}
                <button onClick={() => setExpanded(false)} aria-label="Close full-screen plan"
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-foreground/15 hover:bg-primary-foreground/25 text-primary-foreground btn-anim cursor-pointer"
                  data-testid="close-plan-modal">
                  <X size={20} weight="bold" />
                </button>
              </div>
            </div>
          </div>

          {/* Scrollable two-column body */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-6xl mx-auto w-full px-5 py-6">
              {intro && <div className="whitespace-pre-wrap leading-relaxed text-[15px] text-foreground/80 mb-5 max-w-3xl" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fmt(intro)) }} />}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
                {/* LEFT (main): services */}
                <div className="lg:col-span-2 space-y-5">
                  <section className="bg-card border border-border/70 rounded-2xl shadow-soft overflow-hidden">
                    <div className="px-5 py-3 border-b border-border/60 flex items-center gap-2">
                      <Users size={16} weight="fill" className="text-primary" />
                      <h3 className="text-[14px] font-semibold text-foreground">Services ({cards.length})</h3>
                    </div>
                    {servicesPanel('max-h-[calc(100vh-260px)]')}
                  </section>
                </div>

                {/* RIGHT (side): budget + timeline stacked */}
                <div className="space-y-5">
                  {budget && (
                    <section className="bg-card border border-border/70 rounded-2xl shadow-soft overflow-hidden">
                      <div className="px-5 py-3 border-b border-border/60 flex items-center gap-2">
                        <CurrencyCircleDollar size={16} weight="fill" className="text-primary" />
                        <h3 className="text-[14px] font-semibold text-foreground">Budget</h3>
                      </div>
                      {budgetPanel('max-h-[420px]')}
                    </section>
                  )}
                  {timeline.length > 0 && (
                    <section className="bg-card border border-border/70 rounded-2xl shadow-soft overflow-hidden">
                      <div className="px-5 py-3 border-b border-border/60 flex items-center gap-2">
                        <Clock size={16} weight="fill" className="text-primary" />
                        <h3 className="text-[14px] font-semibold text-foreground">Timeline ({timeline.length})</h3>
                      </div>
                      {timelinePanel('max-h-[420px]')}
                    </section>
                  )}

                  {/* Suggestions + adjustments */}
                  {(suggestions.length > 0 || onQuickReply) && (
                    <section className="bg-card border border-border/70 rounded-2xl shadow-soft p-4">
                      <h3 className="text-[13px] font-semibold text-foreground mb-2.5">Adjust this plan</h3>
                      <div className="flex flex-wrap gap-2">
                        {suggestions.map((sg, i) => (
                          <button key={`s-${i}`} onClick={() => onQuickReply?.(sg.message)}
                            className="text-[12px] font-semibold rounded-full bg-accent-coral/10 text-accent-coral px-3.5 py-2 hover:bg-accent-coral hover:text-primary-foreground btn-anim cursor-pointer inline-flex items-center gap-1.5">
                            <Plus size={11} weight="bold" /> {sg.label}
                          </button>
                        ))}
                        {onQuickReply && ["Swap a provider", "Add more services", "Lower the budget", "Show alternatives"].map((r, i) => (
                          <button key={`q-${i}`} onClick={() => onQuickReply(r)}
                            className="text-[12px] font-medium rounded-full bg-secondary text-primary px-3.5 py-2 hover:bg-primary hover:text-primary-foreground btn-anim cursor-pointer">{r}</button>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sticky book bar */}
          <div className="bg-foreground text-background shrink-0 shadow-lift">
            <div className="max-w-6xl mx-auto w-full px-5 py-3.5 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-background/60 flex items-center gap-1.5 mb-0.5">
                  <ShieldCheck size={10} /> {cards.length} services{eventDate ? <> &middot; <CalendarBlank size={10} /> {eventDate}</> : null} &middot; escrow protected &middot; free cancel 48h+
                </p>
                {planTotal && <p className="text-xl font-display font-semibold leading-tight">${planTotal}</p>}
              </div>
              <button onClick={() => onBookAll(cards, eventDate)} disabled={cards.length === 0}
                className="bg-primary text-primary-foreground px-6 py-3 text-[13px] font-semibold hover:bg-primary/90 hover:shadow-lift btn-anim cursor-pointer rounded-xl flex items-center gap-2 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="book-all-button-expanded">
                <Lightning size={15} weight="fill" /> Book entire event
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// Compact month calendar for "when is your event?" questions
function MiniCalendar({ selected, onSelect }) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [yearPick, setYearPick] = useState(false);
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1).getDay();
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const atCurrentMonth = year === today.getFullYear() && m === today.getMonth();
  const fmt = (day) => `${year}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const monthLabel = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-card border border-border/70 rounded-xl p-3 max-w-[270px]" data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-2">
        <button type="button" disabled={atCurrentMonth || yearPick}
          onClick={() => setMonth(new Date(year, m - 1, 1))}
          className={`w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary/60 disabled:opacity-30 btn-anim cursor-pointer ${yearPick ? 'invisible' : ''}`}
          aria-label="Previous month" data-testid="cal-prev">
          <CaretLeft size={12} weight="bold" />
        </button>
        <button type="button" onClick={() => setYearPick(v => !v)}
          className="text-[11px] font-semibold text-foreground hover:text-primary flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-secondary/60 btn-anim cursor-pointer"
          aria-label="Change year" data-testid="cal-title">
          {yearPick ? 'Pick a year' : monthLabel} <CaretDown size={9} weight="bold" className={yearPick ? 'rotate-180' : ''} />
        </button>
        <button type="button" onClick={() => setMonth(new Date(year, m + 1, 1))}
          className={`w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary/60 btn-anim cursor-pointer ${yearPick ? 'invisible' : ''}`}
          aria-label="Next month" data-testid="cal-next">
          <CaretRight size={12} weight="bold" />
        </button>
      </div>
      {yearPick && (
        <div className="grid grid-cols-3 gap-1.5 py-1" data-testid="year-grid">
          {Array.from({ length: 9 }, (_, i) => today.getFullYear() + i).map(y => (
            <button key={y} type="button"
              onClick={() => {
                // Don't land in a past month when jumping back to the current year
                const nm = (y === today.getFullYear() && m < today.getMonth()) ? today.getMonth() : m;
                setMonth(new Date(y, nm, 1));
                setYearPick(false);
              }}
              className={`h-9 rounded-lg text-[11px] font-medium tabular-nums btn-anim cursor-pointer ${
                y === year ? 'bg-primary text-primary-foreground font-bold shadow-soft' : 'text-foreground hover:bg-secondary'}`}
              data-testid={`cal-year-${y}`}>
              {y}
            </button>
          ))}
        </div>
      )}
      {!yearPick && (
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <span key={i} className="text-[9px] font-semibold text-muted-foreground py-0.5">{d}</span>
        ))}
        {Array.from({ length: firstDay }, (_, i) => <span key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const iso = fmt(day);
          const isPast = new Date(year, m, day) < today;
          const isSel = selected === iso;
          return (
            <button key={day} type="button" disabled={isPast}
              onClick={() => onSelect(iso)}
              className={`w-8 h-8 rounded-lg text-[11px] tabular-nums btn-anim cursor-pointer disabled:opacity-25 disabled:cursor-default ${
                isSel ? 'bg-primary text-primary-foreground font-bold shadow-soft'
                      : 'text-foreground hover:bg-secondary'}`}
              data-testid={`cal-day-${iso}`}>
              {day}
            </button>
          );
        })}
      </div>
      )}
      {selected && (
        <p className="text-[10px] text-primary font-medium text-center mt-1.5" data-testid="cal-selected">
          {new Date(`${selected}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
      )}
    </div>
  );
}

// User bubble: multi-answer messages render as "Question (bold): answer", one per line
function UserBubble({ content }) {
  const lines = content.split('\n').filter((l) => l.trim());
  // Legacy multi-answer format ("Question: answer" per line) → show only the answers, numbered
  const qa = lines.map((l) => l.match(/^(.{2,90}?):\s+(.+)$/));
  const legacyAnswers = lines.length > 1 && qa.every(Boolean);
  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        const num = line.match(/^(\d+)\.\s+(.+)$/);
        const answer = num ? num[2] : legacyAnswers ? qa[i][2] : null;
        if (answer) {
          return (
            <div key={i} className="flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-primary-foreground/20 text-[9px] font-bold flex items-center justify-center shrink-0">{num ? num[1] : i + 1}</span>
              {answer}
            </div>
          );
        }
        return <div key={i} className="whitespace-pre-wrap">{line}</div>;
      })}
    </div>
  );
}

// ─── Main Page ───
export default function AIPlannerPage() {
  const { token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [loadingText, setLoadingText] = useState('');
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const loadPlans = useCallback(async () => { try { const r = await axios.get(`${API}/ai-planner/plans`, { headers: { Authorization: `Bearer ${token}` } }); setPlans(r.data); } catch (err) { console.error('Failed to load plans:', err); } }, [token]);

  // Events — named containers that group plan conversations (like ChatGPT Projects)
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [quickChat, setQuickChat] = useState(false); // skip the name-your-event gate for one-off questions
  const [eventNameVal, setEventNameVal] = useState('');
  const [renamingEvent, setRenamingEvent] = useState(null);
  const [collapsedEvents, setCollapsedEvents] = useState({});
  const [mobileNav, setMobileNav] = useState(false);

  // Close the mobile drawer whenever a chat or event is picked
  useEffect(() => { setMobileNav(false); }, [planId, activeEvent, quickChat]);

  const loadEvents = useCallback(async () => { try { const r = await axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } }); setEvents(r.data); } catch (err) { console.error('Failed to load events:', err); } }, [token]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const createEvent = async () => {
    if (!eventNameVal.trim()) return;
    try {
      const r = await axios.post(`${API}/events`, { name: eventNameVal }, { headers: { Authorization: `Bearer ${token}` } });
      setEventNameVal('');
      setActiveEvent(r.data.event_id);
      setQuickChat(false);
      setPlanId(null); setMsgs([]);
      loadEvents();
    } catch (err) { console.error('Failed to create event:', err); }
  };
  const renameEventSave = async (id) => {
    if (!eventNameVal.trim()) { setRenamingEvent(null); return; }
    try { await axios.put(`${API}/events/${id}`, { name: eventNameVal }, { headers: { Authorization: `Bearer ${token}` } }); loadEvents(); } catch (err) { console.error('Failed to rename event:', err); }
    setRenamingEvent(null); setEventNameVal('');
  };
  const deleteEvent = async (id) => {
    try { await axios.delete(`${API}/events/${id}`, { headers: { Authorization: `Bearer ${token}` } }); if (activeEvent === id) setActiveEvent(null); loadEvents(); loadPlans(); } catch (err) { console.error('Failed to delete event:', err); }
    setMenuOpen(null);
  };
  const movePlanToEvent = async (pid, eid) => {
    try { await axios.put(`${API}/ai-planner/plans/${pid}/event`, { event_id: eid }, { headers: { Authorization: `Bearer ${token}` } }); loadPlans(); loadEvents(); } catch (err) { console.error('Failed to move plan:', err); }
    setMenuOpen(null);
  };

  // One conversation row in the sidebar (used inside events and in the ungrouped list)
  const renderPlan = (p) => (
    <div key={p.plan_id} className={`relative group rounded-xl ${planId === p.plan_id ? 'bg-secondary' : 'hover:bg-secondary/50'} transition-colors`}>
      {renaming === p.plan_id ? (
        <div className="px-3 py-2.5 flex items-center gap-1.5">
          <input type="text" value={renameVal} onChange={e => setRenameVal(e.target.value)} autoFocus onKeyDown={e => { if (e.key === 'Enter') renamePlan(p.plan_id); if (e.key === 'Escape') setRenaming(null); }}
            className="flex-1 min-w-0 border border-border bg-card px-2 py-1 text-xs rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40" />
          <button onClick={() => renamePlan(p.plan_id)} className="text-primary hover:text-primary/80 cursor-pointer"><PaperPlaneTilt size={12} /></button>
          <button onClick={() => setRenaming(null)} className="text-muted-foreground cursor-pointer"><X size={12} /></button>
        </div>
      ) : (
        <div className="flex items-center">
          <button onClick={() => openPlan(p.plan_id)} className="flex-1 text-left px-3 py-2.5 min-w-0 min-h-[44px] cursor-pointer" data-testid={`plan-${p.plan_id}`}>
            <div className="flex items-center gap-1.5">
              {p.pinned && <PushPin size={9} weight="fill" className="text-primary shrink-0" />}
              <p className={`text-[11px] truncate font-medium ${planId === p.plan_id ? 'text-primary' : 'text-foreground'}`}>{p.name || p.messages?.[0]?.content?.slice(0, 25) || 'Plan'}...</p>
            </div>
            <p className="text-[9px] text-muted-foreground mt-0.5">{new Date(p.created_at).toLocaleDateString()}</p>
          </button>
          <button onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === p.plan_id ? null : p.plan_id); }}
            className="px-2 py-2.5 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-primary transition-all shrink-0 cursor-pointer">
            <DotsThree size={15} weight="bold" />
          </button>
        </div>
      )}
      {menuOpen === p.plan_id && (
        <div className="absolute right-1 top-9 z-50 w-44 bg-card border border-border/70 shadow-float py-1 rounded-xl" onClick={e => e.stopPropagation()}>
          <button onClick={() => { setRenaming(p.plan_id); setRenameVal(p.name || p.messages?.[0]?.content?.slice(0, 35) || ''); setMenuOpen(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-foreground hover:bg-secondary/60 cursor-pointer"><PencilSimple size={12} className="text-muted-foreground" /> Rename</button>
          <button onClick={() => pinPlan(p.plan_id)} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-foreground hover:bg-secondary/60 cursor-pointer"><PushPin size={12} className={p.pinned ? 'text-primary' : 'text-muted-foreground'} weight={p.pinned ? 'fill' : 'regular'} /> {p.pinned ? 'Unpin' : 'Pin'}</button>
          <button onClick={() => sharePlan(p.plan_id)} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-foreground hover:bg-secondary/60 cursor-pointer"><ShareNetwork size={12} className="text-muted-foreground" /> Share</button>
          {events.length > 0 && (
            <>
              <div className="my-0.5 border-t border-border/70" />
              <p className="px-3 pt-1 pb-0.5 text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">Move to event</p>
              {events.filter(ev => ev.event_id !== p.event_id).map(ev => (
                <button key={ev.event_id} onClick={() => movePlanToEvent(p.plan_id, ev.event_id)} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-foreground hover:bg-secondary/60 cursor-pointer">
                  <FolderSimple size={12} className="text-muted-foreground" /> <span className="truncate">{ev.name}</span>
                </button>
              ))}
              {p.event_id && (
                <button onClick={() => movePlanToEvent(p.plan_id, null)} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-foreground hover:bg-secondary/60 cursor-pointer">
                  <X size={12} className="text-muted-foreground" /> Remove from event
                </button>
              )}
            </>
          )}
          <div className="my-0.5 border-t border-border/70" />
          <button onClick={() => deletePlan(p.plan_id)} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-red-600 hover:bg-red-50 cursor-pointer"><Trash size={12} className="text-red-400" /> Delete</button>
        </div>
      )}
    </div>
  );

  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => { const h = () => setMenuOpen(null); if (menuOpen) document.addEventListener('click', h); return () => document.removeEventListener('click', h); }, [menuOpen]);

  // Animated loading text
  useEffect(() => {
    if (!busy) { setLoadingText(''); return; }
    const steps = ['Analyzing your event...', 'Searching providers...', 'Comparing prices...', 'Building your plan...'];
    let i = 0;
    setLoadingText(steps[0]);
    const t = setInterval(() => { i = (i + 1) % steps.length; setLoadingText(steps[i]); }, 2500);
    return () => clearInterval(t);
  }, [busy]);

  const openPlan = async (id) => { setPlanId(id); try { const r = await axios.get(`${API}/ai-planner/plans/${id}`, { headers: { Authorization: `Bearer ${token}` } }); setMsgs(r.data.messages || []); setActiveEvent(r.data.event_id || null); scroll(); } catch (err) { console.error('Failed to open plan:', err); } };
  const newPlan = () => { setPlanId(null); setMsgs([]); };
  const deletePlan = async (id) => { try { await axios.delete(`${API}/ai-planner/plans/${id}`, { headers: { Authorization: `Bearer ${token}` } }); if (planId === id) newPlan(); loadPlans(); } catch (err) { console.error('Failed to delete plan:', err); } setMenuOpen(null); };
  const renamePlan = async (id) => { if (!renameVal.trim()) return; try { await axios.put(`${API}/ai-planner/plans/${id}/rename`, { name: renameVal }, { headers: { Authorization: `Bearer ${token}` } }); loadPlans(); } catch (err) { console.error('Failed to rename:', err); } setRenaming(null); };
  const pinPlan = async (id) => { try { await axios.put(`${API}/ai-planner/plans/${id}/pin`, {}, { headers: { Authorization: `Bearer ${token}` } }); loadPlans(); } catch (err) { console.error('Failed to pin:', err); } setMenuOpen(null); };
  const sharePlan = (id) => { navigator.clipboard.writeText(`${window.location.origin}/ai-planner?plan=${id}`).catch(() => {}); setMenuOpen(null); };

  const scroll = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 120);

  const bookingAllRef = useRef(false);
  const bookAll = async (cards, eventDate) => {
    if (!cards.length) return;
    if (bookingAllRef.current) return;   // guard against double-click → duplicate bookings
    // Need an event date — the backend now rejects an empty one rather than
    // inventing a day nobody chose. Tell the user instead of failing opaquely.
    if (!eventDate) {
      setMsgs(p => [...p, { role: 'assistant', content: "I need your event date before booking — tell me the date (e.g. 'the event is on August 30') and I'll lock it in." }]);
      return;
    }
    // Only providers with a real bookable service can go in the order. A card the
    // AI produced without a service_id can't be booked — drop it and tell the
    // user rather than silently charging less than the plan showed.
    const bookable = cards.filter(c => c.serviceId);
    const noService = cards.filter(c => !c.serviceId);
    if (!bookable.length) {
      setMsgs(p => [...p, { role: 'assistant', content: "None of these providers have a bookable package set up yet, so I can't check out the whole event. You can message them from their cards above to arrange it." }]);
      return;
    }
    if (noService.length) {
      const names = noService.map(c => c.name).join(', ');
      setMsgs(p => [...p, { role: 'assistant', content: `Heads up: ${names} ${noService.length === 1 ? "doesn't" : "don't"} have an online booking package yet, so ${noService.length === 1 ? "it isn't" : "they aren't"} in this checkout — message ${noService.length === 1 ? 'them' : 'each'} from the cards above to arrange it. Continuing with the rest.` }]);
    }
    bookingAllRef.current = true;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/checkout/book-all`, {
        items: bookable.map(c => ({ provider_id: c.profileId, service_id: c.serviceId, hours: 4, qty: c.qty || (c.unit === 'hr' || c.unit === 'hour' ? 4 : 1) })),
        booking_date: eventDate, origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // The backend may skip providers who blocked the date or would duplicate.
      // Surface that clearly BEFORE sending the customer to pay, so the amount
      // and scope aren't a silent surprise.
      const skipped = res.data.skipped || [];
      if (res.data.url) {
        if (skipped.length) {
          const names = [...new Set(skipped)].join(', ');
          const ok = window.confirm(`Some providers couldn't be booked for ${eventDate} (${names}) — usually they're unavailable that day. Continue to pay for the ${res.data.item_count} remaining service${res.data.item_count === 1 ? '' : 's'} ($${res.data.total})?`);
          if (!ok) { bookingAllRef.current = false; setBusy(false); return; }
        }
        window.location.href = res.data.url; return;  // navigating away; keep guard set
      }
    } catch (err) {
      console.error('Book all failed:', err);
      const detail = err?.response?.data?.detail;
      setMsgs(p => [...p, { role: 'assistant', content: (typeof detail === 'string' && detail) ? detail : 'I had trouble creating the checkout. You can book each provider individually above.' }]);
    } finally { setBusy(false); }
    bookingAllRef.current = false;  // only reached on failure (success navigates away)
  };

  // Roll back the newest question/answer exchange so the user can re-answer
  const undoLast = async () => {
    if (busy || msgs.length < 2) return;
    try {
      if (planId) {
        await axios.delete(`${API}/ai-planner/plans/${planId}/last-exchange`,
          { headers: { Authorization: `Bearer ${token}` } });
      }
      setMsgs(p => p.slice(0, -2));
    } catch (err) { console.error('Undo failed:', err); }
  };

  const send = async (text) => {
    const t = text || input;
    if (!t.trim()) return;
    setMsgs(p => [...p, { role: 'user', content: t }]);
    setInput(''); setBusy(true);
    try {
      const res = await fetch(`${API}/ai-planner/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: t, plan_id: planId, event_id: planId ? undefined : activeEvent }),
      });
      if (!res.ok || !res.body) throw new Error(`stream failed (${res.status})`);
      // Stream the reply in as it's written
      setMsgs(p => [...p, { role: 'assistant', content: '', streaming: true }]);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '', draft = '', shown = 0, finalData = null, lastScroll = 0;
      const setLast = (msg) => setMsgs(p => { const c = [...p]; c[c.length - 1] = msg; return c; });
      // Typewriter: the network delivers text in bursts — reveal it a few
      // characters per frame instead, speeding up as the backlog grows so the
      // display never falls far behind the stream.
      let tick;
      const typed = new Promise((resolve) => {
        tick = setInterval(() => {
          if (shown < draft.length) {
            const backlog = draft.length - shown;
            shown = Math.min(draft.length, shown + Math.max(2, Math.round(backlog / 10)));
            setLast({ role: 'assistant', content: draft.slice(0, shown), streaming: true });
            const now = Date.now();
            if (now - lastScroll > 400) { lastScroll = now; scroll(); }
          } else if (finalData !== null) {
            clearInterval(tick);
            resolve();
          }
        }, 24);
      });
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const events = buf.split('\n\n');
          buf = events.pop();
          for (const ev of events) {
            const line = ev.split('\n').find(l => l.startsWith('data: '));
            if (!line) continue;
            const d = JSON.parse(line.slice(6));
            if (d.t) draft += d.t;
            if (d.done) finalData = d;
          }
        }
        if (finalData === null) finalData = { plan_id: planId, content: draft };
        await typed;
      } finally { clearInterval(tick); }
      setPlanId(finalData.plan_id);
      setLast({ role: 'assistant', content: finalData.content });
      loadPlans();
      loadEvents();
    } catch (err) {
      console.error('AI planner send failed:', err);
      setMsgs(p => {
        const c = [...p];
        if (c[c.length - 1]?.streaming) c.pop();
        return [...c, { role: 'assistant', content: 'Something went wrong. Please try again.' }];
      });
    } finally { setBusy(false); scroll(); }
  };

  // Auto-send a message handed off from the landing page hero chat
  const pendingHandled = useRef(false);
  useEffect(() => {
    if (pendingHandled.current) return;
    const pending = sessionStorage.getItem('bookr_pending_plan');
    if (pending) {
      pendingHandled.current = true;
      sessionStorage.removeItem('bookr_pending_plan');
      send(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="ai-planner-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
        <div className="relative bg-card border border-border/70 grid md:grid-cols-[220px_1fr] overflow-hidden rounded-2xl shadow-soft" style={{ height: 'calc(100vh - 100px)' }}>

          {/* Mobile drawer backdrop */}
          {mobileNav && <div className="absolute inset-0 z-30 bg-foreground/30 md:hidden" onClick={() => setMobileNav(false)} />}

          {/* ─── Sidebar (drawer on mobile) ─── */}
          <div className={`border-r border-border overflow-y-auto flex-col ${mobileNav ? 'flex absolute inset-y-0 left-0 z-40 w-72 max-w-[85%] bg-card shadow-lift' : 'hidden bg-background/60'} md:flex md:static md:z-auto md:w-auto md:max-w-none md:bg-background/60 md:shadow-none`} data-testid="plans-sidebar">
            <div className="p-3 border-b border-border space-y-2">
              <button onClick={() => { setActiveEvent(null); setQuickChat(false); setEventNameVal(''); newPlan(); }}
                className="w-full flex items-center gap-2 px-3 py-3 min-h-[44px] text-xs font-semibold text-primary-foreground bg-primary hover:bg-primary/90 btn-anim cursor-pointer rounded-xl shadow-soft hover:shadow-lift" data-testid="new-event-button">
                <Plus size={13} weight="bold" /> New event
              </button>
              <button onClick={() => { setActiveEvent(null); setQuickChat(true); newPlan(); }} className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-medium text-muted-foreground hover:text-primary hover:bg-secondary/60 btn-anim cursor-pointer rounded-xl" data-testid="new-plan-button">
                <ChatCircle size={13} /> New chat
              </button>
              {/* Once vendors are booked, jump into day-of coordination */}
              <a href="/coordinator" className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-semibold text-primary bg-primary/5 border border-primary/20 hover:bg-primary/10 btn-anim cursor-pointer rounded-xl no-underline" data-testid="planner-coordinator-link">
                <Sparkle size={13} weight="fill" /> Event Coordinator
              </a>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {plans.length === 0 && events.length === 0 && (
                <div className="px-4 py-8 text-center">
                  <div className="w-9 h-9 bg-secondary rounded-full flex items-center justify-center mx-auto mb-2">
                    <Lightning size={16} className="text-primary" />
                  </div>
                  <p className="text-[11px] text-muted-foreground">Your event plans will appear here</p>
                </div>
              )}

              {/* Events — each groups its conversations */}
              {events.map(ev => {
                const evPlans = plans.filter(p => p.event_id === ev.event_id).sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
                const collapsed = collapsedEvents[ev.event_id];
                return (
                  <div key={ev.event_id} className="rounded-xl" data-testid={`event-${ev.event_id}`}>
                    {renamingEvent === ev.event_id ? (
                      <div className="px-2 py-1.5 flex items-center gap-1.5">
                        <input type="text" value={eventNameVal} onChange={e => setEventNameVal(e.target.value)} autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') renameEventSave(ev.event_id); if (e.key === 'Escape') setRenamingEvent(null); }}
                          className="flex-1 min-w-0 border border-border bg-card px-2 py-1 text-xs rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40" />
                        <button onClick={() => renameEventSave(ev.event_id)} className="text-primary cursor-pointer"><Check size={13} weight="bold" /></button>
                        <button onClick={() => setRenamingEvent(null)} className="text-muted-foreground cursor-pointer"><X size={13} /></button>
                      </div>
                    ) : (
                      <div className={`relative group flex items-center rounded-xl transition-colors ${activeEvent === ev.event_id && !planId ? 'bg-secondary' : 'hover:bg-secondary/50'}`}>
                        <button
                          onClick={() => { setActiveEvent(ev.event_id); setCollapsedEvents(c => ({ ...c, [ev.event_id]: activeEvent === ev.event_id ? !collapsed : false })); }}
                          className="flex-1 flex items-center gap-2 px-2.5 py-2.5 min-h-[40px] min-w-0 text-left cursor-pointer" data-testid={`event-row-${ev.event_id}`}>
                          {collapsed ? <CaretRight size={11} className="text-muted-foreground shrink-0" /> : <CaretDown size={11} className="text-muted-foreground shrink-0" />}
                          <FolderSimple size={14} weight={activeEvent === ev.event_id ? 'fill' : 'regular'} className={`shrink-0 ${activeEvent === ev.event_id ? 'text-primary' : 'text-muted-foreground'}`} />
                          <span className={`text-[11px] font-semibold truncate ${activeEvent === ev.event_id ? 'text-primary' : 'text-foreground'}`}>{ev.name}</span>
                          <span className="text-[9px] text-muted-foreground shrink-0">{evPlans.length || ''}</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === `evt:${ev.event_id}` ? null : `evt:${ev.event_id}`); }}
                          className="px-2 py-2 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-primary transition-all shrink-0 cursor-pointer" aria-label="Event options">
                          <DotsThree size={15} weight="bold" />
                        </button>
                        {menuOpen === `evt:${ev.event_id}` && (
                          <div className="absolute right-1 top-9 z-50 w-40 bg-card border border-border/70 shadow-float py-1 rounded-xl" onClick={e => e.stopPropagation()}>
                            <button onClick={() => { setRenamingEvent(ev.event_id); setEventNameVal(ev.name); setMenuOpen(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-foreground hover:bg-secondary/60 cursor-pointer"><PencilSimple size={12} className="text-muted-foreground" /> Rename</button>
                            <button onClick={() => { setActiveEvent(ev.event_id); newPlan(); setMenuOpen(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-foreground hover:bg-secondary/60 cursor-pointer"><Plus size={12} className="text-muted-foreground" /> New chat here</button>
                            <div className="my-0.5 border-t border-border/70" />
                            <button onClick={() => deleteEvent(ev.event_id)} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-red-600 hover:bg-red-50 cursor-pointer"><Trash size={12} className="text-red-400" /> Delete event</button>
                          </div>
                        )}
                      </div>
                    )}
                    {!collapsed && evPlans.length > 0 && (
                      <div className="ml-4 pl-2 border-l border-border/60 space-y-0.5">
                        {evPlans.map(renderPlan)}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Ungrouped conversations */}
              {(() => {
                const loose = plans.filter(p => !p.event_id).sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
                if (!loose.length) return null;
                return (
                  <>
                    {events.length > 0 && <p className="px-2.5 pt-3 pb-1 text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">Chats</p>}
                    {loose.map(renderPlan)}
                  </>
                );
              })()}
            </div>
          </div>

          {/* ─── Chat ─── */}
          <div className="flex flex-col min-h-0">
            {/* Header */}
            <div className="px-5 py-2.5 border-b border-border flex items-center justify-between bg-card shrink-0">
              <div className="flex items-center gap-3">
                <button onClick={() => setMobileNav(true)} aria-label="Open chats and events"
                  className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary/60 btn-anim cursor-pointer -ml-1"
                  data-testid="planner-mobile-nav">
                  <FolderSimple size={18} />
                </button>
                <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center shadow-soft">
                  <Lightning size={14} weight="fill" className="text-primary-foreground" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-foreground">Bookr AI</p>
                  <p className="text-[9px] text-muted-foreground">
                    {activeEvent && events.find(e => e.event_id === activeEvent)
                      ? <><FolderSimple size={9} className="inline mr-0.5 -mt-px" />{events.find(e => e.event_id === activeEvent).name}</>
                      : <>Event planner &middot; Budget builder &middot; Provider matching</>}
                  </p>
                </div>
              </div>
              <TrustMini />
            </div>

            {/* Messages */}
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 space-y-4 bg-background/50" data-testid="planner-chat-area">

              {/* Step 1 — name your event (shown until an event exists or quick chat is chosen) */}
              {msgs.length === 0 && !activeEvent && !quickChat && (
                <div className="flex flex-col items-center justify-center h-full" data-testid="event-gate">
                  <div className="max-w-md w-full text-center fade-up">
                    <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lift">
                      <FolderSimple size={26} weight="fill" className="text-primary-foreground" />
                    </div>
                    <h2 className="text-2xl font-display font-semibold tracking-tight text-foreground mb-1.5">Name your event</h2>
                    <p className="text-[13px] text-muted-foreground max-w-sm mx-auto leading-relaxed mb-6">Every plan starts with an event. Give it a name and I'll help you plan the whole thing — providers, budget, timeline.</p>
                    <div className="flex items-center gap-2 max-w-sm mx-auto">
                      <input type="text" value={eventNameVal} onChange={e => setEventNameVal(e.target.value)} autoFocus placeholder="Sarah's wedding, office party, dad's 60th…"
                        onKeyDown={e => { if (e.key === 'Enter') createEvent(); }}
                        className="flex-1 min-w-0 h-12 border border-border bg-card px-4 text-sm rounded-xl shadow-soft focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground/60"
                        data-testid="new-event-name-input" />
                      <button onClick={createEvent} disabled={!eventNameVal.trim()}
                        className="bg-primary text-primary-foreground w-12 h-12 rounded-xl flex items-center justify-center btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 disabled:opacity-30 cursor-pointer shrink-0"
                        aria-label="Create event" data-testid="create-event-confirm">
                        <ArrowRight size={18} weight="bold" />
                      </button>
                    </div>
                    <button onClick={() => setQuickChat(true)} className="mt-6 text-[11px] text-muted-foreground hover:text-primary transition-colors cursor-pointer" data-testid="skip-to-chat">
                      Just have a quick question? Start a chat instead
                    </button>
                  </div>
                </div>
              )}

              {/* Welcome */}
              {msgs.length === 0 && (activeEvent || quickChat) && (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="max-w-lg text-center mb-8 fade-up">
                    <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lift">
                      <Lightning size={26} weight="fill" className="text-primary-foreground" />
                    </div>
                    {activeEvent && events.find(e => e.event_id === activeEvent) ? (
                      <>
                        <h2 className="text-2xl font-display font-semibold tracking-tight text-foreground mb-1.5">Let's plan {events.find(e => e.event_id === activeEvent).name}</h2>
                        <p className="text-[13px] text-muted-foreground max-w-sm mx-auto leading-relaxed">Tell me the date, guest count and budget — or pick a starting point below — and I'll build your plan.</p>
                      </>
                    ) : (
                      <>
                        <h2 className="text-2xl font-display font-semibold tracking-tight text-foreground mb-1.5">What are you planning?</h2>
                        <p className="text-[13px] text-muted-foreground max-w-sm mx-auto leading-relaxed">Pick an event type or describe what you need. I'll find the best providers, build a plan, and handle everything.</p>
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-w-lg w-full mb-6">
                    {STARTERS.map((s, si) => (
                      <button key={s.label} onClick={() => send(s.prompt)}
                        className="bg-card border border-border/70 px-4 py-3.5 min-h-[44px] text-left shadow-soft card-lift cursor-pointer rounded-xl group fade-up"
                        style={{ animationDelay: `${si * 50}ms` }}
                        data-testid={`starter-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                        <s.Icon size={22} weight="regular" className="text-primary mb-1.5" />
                        <span className="text-[12px] font-semibold text-foreground group-hover:text-primary transition-colors block leading-tight">{s.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="bg-secondary/60 rounded-xl px-4 py-3 max-w-lg w-full fade-up" style={{ animationDelay: '300ms' }}>
                    <p className="text-[11px] text-muted-foreground text-center">
                      <span className="font-medium text-primary">Try:</span> "Wedding for 80 guests, $8k budget. Need venue, DJ, photographer, and catering."
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 mt-6 text-[10px] text-muted-foreground fade-up" style={{ animationDelay: '350ms' }}>
                    <span className="flex items-center gap-1"><ShieldCheck size={11} weight="fill" /> Real providers, real prices</span>
                    <span className="flex items-center gap-1"><Sparkle size={11} weight="fill" /> Powered by Claude AI</span>
                    <span className="flex items-center gap-1"><Clock size={11} /> ~30 sec response</span>
                  </div>
                </div>
              )}

              {/* Messages */}
              {msgs.map((msg, i) => {
                // While streaming, keep the bubble hidden until the first text
                // arrives — otherwise an empty card shows above the typing dots.
                if (msg.streaming && !msg.content) return null;
                const isLast = i === msgs.length - 1;
                // Offer "change my answers" under the newest answered question round
                const canUndo = msg.role === 'user' && i === msgs.length - 2 && !busy
                  && msgs[i + 1]?.role === 'assistant' && !msgs[i + 1]?.streaming
                  && msgs[i - 1]?.role === 'assistant' && (msgs[i - 1]?.content || '').includes('OPTION_BUTTONS[');
                return (
                  <React.Fragment key={i}>
                  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2.5 fade-up`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 bg-primary rounded-xl flex items-center justify-center shrink-0 mt-0.5 shadow-soft">
                        <Lightning size={11} weight="fill" className="text-primary-foreground" />
                      </div>
                    )}
                    <div className={`${
                      msg.role === 'user'
                        ? 'max-w-[65%] bg-primary text-primary-foreground px-4 py-2.5 rounded-2xl rounded-br-sm text-[13px] shadow-soft'
                        : 'max-w-[88%] text-[13px]'
                    }`}>
                      {msg.role === 'assistant' ? (
                        <div className="bg-card border border-border/70 rounded-2xl rounded-tl-sm px-5 py-4 shadow-soft">
                          <AiMessageBubble
                            content={msg.content}
                            streaming={msg.streaming}
                            onBookAll={bookAll}
                            onQuickReply={isLast && !busy ? send : null}
                            eventId={activeEvent}
                          />
                        </div>
                      ) : <UserBubble content={msg.content} />}
                    </div>
                  </div>
                  {canUndo && (
                    <div className="flex justify-end -mt-2">
                      <button onClick={undoLast}
                        className="text-[11px] text-muted-foreground hover:text-primary flex items-center gap-1 btn-anim cursor-pointer px-2 py-1.5"
                        data-testid="change-answers-button">
                        <ArrowCounterClockwise size={11} /> Change my answers
                      </button>
                    </div>
                  )}
                  </React.Fragment>
                );
              })}

              {/* Loading (only until the stream starts producing text) */}
              {busy && !(msgs[msgs.length - 1]?.streaming && msgs[msgs.length - 1]?.content) && (
                <div className="flex gap-2.5 fade-up">
                  <div className="w-7 h-7 bg-primary rounded-xl flex items-center justify-center shrink-0 shadow-soft">
                    <Lightning size={11} weight="fill" className="text-primary-foreground" />
                  </div>
                  <div className="bg-card border border-border/70 px-5 py-4 rounded-2xl rounded-tl-sm shadow-soft">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce" />
                        <div className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                        <div className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                      </div>
                      <span className="text-[11px] text-muted-foreground animate-pulse">{loadingText}</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <div className="border-t border-border bg-card px-4 py-3 shrink-0" data-testid="chat-input-area">
              <form onSubmit={e => { e.preventDefault(); send(); }} className="flex items-center gap-2 bg-background border border-border rounded-full pl-1.5 pr-1.5 py-1.5 focus-within:ring-2 focus-within:ring-primary/40 transition-shadow shadow-soft">
                <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)} disabled={busy || (msgs.length === 0 && !activeEvent && !quickChat)}
                  placeholder={msgs.length === 0 && !activeEvent && !quickChat ? "Name your event above to get started…" : msgs.length === 0 ? "Describe your event or pick above..." : "Reply, ask a follow-up, or adjust the plan..."}
                  className="flex-1 bg-transparent px-3.5 py-2 text-sm focus:outline-none placeholder:text-muted-foreground/70 text-foreground min-w-0"
                  data-testid="planner-input" />
                <button type="submit" disabled={busy || !input.trim()}
                  className="bg-primary text-primary-foreground w-11 h-11 rounded-full flex items-center justify-center hover:bg-primary/90 hover:shadow-lift btn-anim cursor-pointer disabled:opacity-20 shrink-0 shadow-soft"
                  aria-label="Send message"
                  data-testid="planner-send-button">
                  <PaperPlaneRight size={17} weight="fill" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
